import { JSX, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SearchFilterContentTypes, SearchOrigins } from 'search-gateway-client';

import { Close, Search as SearchIcon } from '@videoblocks/react-icons';

import {
    NavSearchContext,
    NavSearchContextProvider,
    NavSearchProps,
    StatsigExperimentContext,
} from '../context/NavSearchContext';
import { CrossAssetNavSuggestions } from '../types/shared';
import useMedia from '../utils/useMedia';
import { saveSearchHistory } from './AutosuggestElements/utils';
import CrossAssetNavSearchBox from './NavSearchElements/CrossAssetNavSearchBox';
import NavSearchBox from './NavSearchElements/NavSearchBox';
import NavSearchButton from './NavSearchElements/NavSearchButton';
import NavSearchDropdown from './NavSearchElements/NavSearchDropdown';
import { cleanSearchKeywords } from './NavSearchElements/searchUtils';

interface NavSearchExtraProps {
    compact?: boolean;
    onCollapse?: () => void;
    showCloseButton?: boolean;
}

export default function NavSearch(props: NavSearchProps & NavSearchExtraProps): JSX.Element {
    const {
        onSubmit,
        userHasAnySubscription,
        useSnapshots,
        searchOrigin: searchOriginFromProps = SearchOrigins.TOP_NAV,
        className,
        compact = false,
        onCollapse,
        showCloseButton = false,
    } = props;
    const formRef = useRef<HTMLFormElement>(null);

    const [contentType, setContentType] = useState(props.contentType);
    const [searchTerm, setSearchTerm] = useState('');

    const handleCloseClick = (): void => {
        if (searchTerm.trim()) {
            setSearchTerm('');
        } else {
            onCollapse?.();
        }
    };
    const context = useContext(StatsigExperimentContext);

    const makeEmptySuggestions = (): CrossAssetNavSuggestions => ({
        keywords: [],
        relatedCollections: [],
        latestCollections: [],
        trendingCollections: [],
        relatedContentTypesAndKeywords: [],
    });

    const [suggestions, setSuggestions] = useState<CrossAssetNavSuggestions>(makeEmptySuggestions());

    const clearSuggestions = useCallback(() => setSuggestions(makeEmptySuggestions()), []);

    // i.e. tailwind md breakpoint https://storywind.netlify.app/?path=/story/design-grids--page#medium-672-1024px
    const isMediumScreen = useMedia('(min-width: 672px)', true);
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setContentType(props.contentType);
    }, [props.contentType]);

    const handleDropdownChange = (contentType: string) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setContentType(contentType as SearchFilterContentTypes);
    };
    const handleSubmit = (
        contentTypeSearched: SearchFilterContentTypes = contentType,
        value: string = searchTerm,
        searchOriginOverride?: SearchOrigins,
    ) => {
        const cleanedSearchKeywords = cleanSearchKeywords(value);
        const searchOrigin = searchOriginOverride ?? searchOriginFromProps;
        if (context?.isSearchHistoryEnabled) {
            saveSearchHistory(contentTypeSearched, cleanedSearchKeywords);
        }
        onSubmit(contentTypeSearched, cleanedSearchKeywords, searchOrigin);
    };

    return (
        <NavSearchContextProvider {...props}>
            <NavSearchContext.Consumer>
                {({ setShowSuggestions }) => (
                    <div className={`flex flex-col xl:w-full ${className ?? 'lg:w-72'} font-inter`}>
                        <form
                            ref={formRef}
                            className={
                                compact
                                    ? 'w-full max-w-[1034px] mx-auto h-12 px-2 bg-white rounded-full border border-solid border-gray-400 justify-between items-center inline-flex'
                                    : 'w-full h-10 px-4 py-2 bg-white rounded-3xl border border-solid border-gray-500 justify-between items-center inline-flex'
                            }
                            onSubmit={(event) => {
                                event.preventDefault();
                                setShowSuggestions(false);
                                handleSubmit(contentType, searchTerm);
                            }}
                        >
                            <div
                                className={
                                    compact
                                        ? 'w-full h-8 justify-start items-center gap-2 inline-flex'
                                        : 'w-full h-6 justify-start items-center gap-0.5 inline-flex'
                                }
                            >
                                <NavSearchDropdown
                                    onChange={handleDropdownChange}
                                    value={contentType}
                                    useSnapshots={useSnapshots}
                                    compact={compact}
                                />
                                {!compact && <div className="xl:w-px lg:w-px md:w-px h-6 bg-gray-500" />}
                                {isMediumScreen ? (
                                    <CrossAssetNavSearchBox
                                        contentType={contentType}
                                        setSearchTerm={setSearchTerm}
                                        onSubmit={handleSubmit}
                                        searchTerm={searchTerm}
                                        suggestions={suggestions}
                                        setSuggestions={setSuggestions}
                                        clearSuggestions={clearSuggestions}
                                    />
                                ) : (
                                    <NavSearchBox
                                        contentType={contentType}
                                        onChange={setSearchTerm}
                                        onSubmit={handleSubmit}
                                        searchTerm={searchTerm}
                                        userHasAnySubscription={userHasAnySubscription}
                                    />
                                )}
                            </div>
                            {compact && showCloseButton && (
                                <button
                                    type="button"
                                    onClick={handleCloseClick}
                                    aria-label="Clear or close search"
                                    className="flex-shrink-0 p-2 mr-1 text-gray-500 hover:text-gray-700"
                                >
                                    <Close className="w-4 h-4 fill-current" />
                                </button>
                            )}
                            {compact && !showCloseButton && (
                                <button
                                    type="submit"
                                    aria-label="Submit search"
                                    className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-gray-900 hover:bg-black text-white"
                                >
                                    <SearchIcon className="w-4 h-4 fill-current" />
                                </button>
                            )}
                            {!compact && <NavSearchButton />}
                        </form>
                    </div>
                )}
            </NavSearchContext.Consumer>
        </NavSearchContextProvider>
    );
}
